import React, { useState, useEffect, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { useParams, useNavigate } from "react-router-dom";
import Detail from "./components/detail";
import {
  GenericView,
  SearchInput,
  ActionButtons,
} from "../../../../components/common/table";
import LeadsForm from "../../../Leads/components/LeadsForm";
import TaskForm from "../../../Task/components/TaskForm";
import { createTask, updateTask, deleteTask } from "../../../Task/api/taskApi";
import { useLeadsForm } from "../../../Leads/hooks/useLeadsForm";
import InvoiceForm from "../../../Invoice/components/InvoiceForm";
import { getInvoice } from "../../../Invoice/api/invoiceApi";
import PaymentForm from "../../../Payment/components/PaymentForm";
import { validateInvoiceForm } from "../../../../utils/validation";
import ExpenseForm from "../../../Expense/components/ExpenseForm.jsx";
import {
  createExpense,
  updateExpense,
  deleteExpense,
} from "../../../Expense/api/expenseApi";
import { transformToOptions } from "../../../../utils/dataTransformers";
import api from "../../../../api";
import Alert from "../../../../components/Alert/Alert";
import Loading from "../../../../components/Loading/Loading";
import ConfirmationModal from "../../../../components/common/ConfirmationModal";
import { ArrowLeft, FileText } from "lucide-react";
import { useAuth } from "../../../../contexts/AuthProvider";

// Flattens a raw cell value for spreadsheet/PDF output. Column render()
// functions return JSX and are deliberately not used here.
const stringifyCell = (value) => {
  if (value == null) return "";
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object") {
    const composed = `${value.firstName || ""} ${value.lastName || ""}`.trim();
    return (
      value.name ?? value.fullName ?? value.label ?? composed ?? JSON.stringify(value)
    ) || JSON.stringify(value);
  }
  return String(value);
};

// Renders a comma-separated string as a wrapped list of badges. The projects,
// proposals and invoices tabs each had their own copy of this shape.
const renderBadgeList = (raw, badgeClass, titlePrefix = "") => {
  const tags = String(raw ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (tags.length === 0) return <span className="text-muted">-</span>;
  return (
    <span className="d-inline-flex flex-wrap gap-1" style={{ maxWidth: "180px" }}>
      {tags.map((tag, i) => (
        <span
          key={i}
          className={`badge ${badgeClass} text-wrap`}
          title={`${titlePrefix}${tag}`}
          style={{ fontSize: "0.7rem" }}
        >
          {tag}
        </span>
      ))}
    </span>
  );
};

const capitalize = (text) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

// Empty Invoice form shape. invoiceTo / invoiceIssued MUST be present here:
// PUT /crm/invoices/{id} overwrites both unconditionally, so a payload that
// omits them nulls the saved values. Keep in sync with src/pages/Invoice/index.jsx.
const buildEmptyInvoiceForm = (companyId) => ({
  client: companyId ? String(companyId) : '',
  projectName: '',
  projectId: null,
  leadId: null,
  billDate: new Date().toISOString().split('T')[0],
  dueDate: '',
  description: '',
  jurnalNumber: '',
  invoiceTo: '',
  invoiceIssued: '',
  tax: 0,
  applyTax: false,
  documents: [],
  status: 'Draft',
  items: [{ description: '', quantity: 1, unitPrice: 0, tax: 0, total: 0 }],
  subtotal: 0,
  dppLainnya: 0,
  total: 0,
  amountReceived: 0,
  message: '',
  notes: '',
  terms: '',
  paymentMethod: '',
  bankAccount: '',
});

// Matches MAX_FILE_SIZE_MB in src/pages/Invoice/index.jsx — guards against the
// server rejecting the upload with an opaque MULTIPART_ERROR.
const MAX_INVOICE_FILE_SIZE_MB = 5;

const Clients = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const tabDataRequestIdRef = useRef(0); // guards against out-of-order tab-fetch responses when switching tabs rapidly
  const decodedCompanyName = decodeURIComponent(id);
  const [activeTab, setActiveTab] = useState("detail");
  const [searchQuery, setSearchQuery] = useState("");
  const [clientData, setClientData] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [tabData, setTabData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [isInvoiceEditMode, setIsInvoiceEditMode] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfTitle, setPdfTitle] = useState("");
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isPaymentEditMode, setIsPaymentEditMode] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentFormData, setPaymentFormData] = useState({ client: '', invoice: '', paymentMethod: '', paymentDate: new Date().toISOString().split('T')[0], amount: '', note: '', file: null });
  const [paymentFormErrors, setPaymentFormErrors] = useState({});
  const [invoiceFormData, setInvoiceFormData] = useState(() =>
    buildEmptyInvoiceForm(),
  );
  const [invoiceFormErrors, setInvoiceFormErrors] = useState({});
  const [isInvoiceSubmitting, setIsInvoiceSubmitting] = useState(false);
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);

  // ✅ TASK UPDATE PERMISSION: Check if user can view task update date
  const { can } = useAuth();
  const canViewTaskUpdate = can("canviewtaskupdate", "task");

  // Same capability gates the standalone Leads / Task / Invoice pages apply.
  // Without these the company tabs would hand out CRUD the owning page denies.
  const canLeads = (capabilityKey) => can(capabilityKey, "leads");
  const canTask = (capabilityKey) => can(capabilityKey, "task");
  const canInvoice = (capabilityKey) => can(capabilityKey, "invoice");

  // Column visibility state for each tab
  const [visibleColumns, setVisibleColumns] = useState({
    leads: [
      "projectName",
      "service",
      "standard",
      "stage",
      "pic",
      "email",
      "contactNumber",
      "assignUser",
      "createdBy",
      "createdAt",
      "status",
      "proposalStatus",
      "leadSource",
      "department",
      "updatedAt",
      "taskUpdateDate",
      "taskUpdateBy",
      // Company-level values, identical on every row inside a company page.
      // Defined in `columns` for parity with the Leads list and toggleable
      // from the column picker, but off by default: "tagging", "sector",
      // "partnerType", "branch".
    ],
    projects: [
      "projectCode",
      "tagging",
      "projectName",
      "service",
      "standard",
      "branch",
      "partnerType",
      "projectDate",
      "validUntil",
      "yearLeft",
      "departmentName",
      "assignedTo",
      "amount",
      "auditDates",
      "createdAt",
    ],
    proposals: [
      "proposal",
      "tagging",
      "proposalDate",
      "validUntil",
      "partnerType",
      "sector",
      "service",
      "standard",
      "amount",
      "createdBy",
      "branch",
      "department",
      "assignedTo",
      "approver",
      "createdAt",
      "updatedAt",
      "statusDesc",
      "statusProposalDesc",
      "projectStatus",
      "proposalStage",
      "stageUpdatedAt",
    ],
    invoices: [
      "uploadedPdf",
      "invoiceNumber",
      "jurnalNumber",
      "departmentName",
      "client",
      "tagging",
      "description",
      "projectName",
      "invoiceTo",
      "invoiceIssued",
      "proposalCode",
      "subtotal",
      "ppn",
      "totalInvoiced",
      "paymentReceived",
      "due",
      "balance",
      "billDate",
      "dueDate",
      "assignedUserName",
      "createdByName",
      "updatedByName",
      "serviceName",
      "status",
    ],
    documents: [
      "documentName",
      "type",
      "category",
      "source",
      "sourceCode",
      "uploadedDate",
      "uploadedBy",
      "size",
      "fileType",
      "download",
    ],
    tasks: [
      "title",
      "contactName",
      "projectName",
      "assignedUser",
      "type",
      "description",
      "fileName",
      "priority",
      "status",
      "dueDate",
      "createdAt",
      "taskUpdateBy",
      ...(canViewTaskUpdate ? ["updatedAt"] : []),
    ],
    expenses: [
      "expenseDate",
      "projectName",
      "categoryName",
      "description",
      "fileName",
      "amount",
    ],
    payments: [
      "id",
      "invoiceNumber",
      "paymentMethod",
      "paymentDate",
      "amount",
      "note",
      "createdBy",
      "createdAt",
      "updatedBy",
      "updatedAt",
      "documentName",
    ],
  });

  // Update visible columns when task update permission changes
  useEffect(() => {
    setVisibleColumns((prev) => ({
      ...prev,
      tasks: [
        "title",
        "contactName",
        "projectName",
        "assignedUser",
        "type",
        "description",
        "fileName",
        "priority",
        "status",
        "dueDate",
        "createdAt",
        "taskUpdateBy",
        ...(canViewTaskUpdate ? ["updatedAt"] : []),
      ],
    }));
  }, [canViewTaskUpdate]);

  const {
    formData,
    formErrors,
    serviceSections,
    uploadedFiles,
    fetchStandardsByServiceId,
    handleInputChange,
    handleTextChange,
    handleFormSubmit,
    addServiceSection,
    deleteServiceSection,
    onDrop,
    removeFile,
    resetForm,
    options,
    fetchContactOptions,
    standardsOptionsMap,
    initializeServiceSections,
    isSubmitting: isLeadSubmitting,
  } = useLeadsForm();

  // Combine options from API with additional options
  // Fetch company name for header
  const [companyName, setCompanyName] = useState("");
  useEffect(() => {
    const fetchCompanyName = async () => {
      try {
        const response = await api.get(`/public/get-company-name/${id}`);
        setCompanyName(response.data);
      } catch (error) {}
    };
    fetchCompanyName();
  }, [id]);

  // The company this page is scoped to, in the { value, label } shape the
  // client search selects expect. Their options load on demand, so a
  // preselected id with no label renders as a blank (and locked) field.
  const presetClient = useMemo(() => {
    if (!clientData?.companyId) return null;
    return {
      value: String(clientData.companyId),
      label:
        companyName ||
        clientData.companyName ||
        clientData.company?.companyName ||
        decodedCompanyName ||
        '',
    };
  }, [clientData, companyName, decodedCompanyName]);

  // Seed the current company into companyOptions so LeadsForm's CompanySection
  // can resolve its label without waiting for (or depending on) the full list.
  const allOptions = useMemo(() => {
    const merged = { ...options };
    if (!presetClient) return merged;
    const existing = merged.companyOptions || [];
    const alreadyPresent = existing.some(
      (option) => String(option.value) === presetClient.value,
    );
    if (!alreadyPresent) {
      merged.companyOptions = [
        {
          value: presetClient.value,
          label: presetClient.label,
          name: presetClient.label,
          website: clientData?.website || '',
          contactNumber: clientData?.contactNumber || clientData?.phone || '',
          address: clientData?.address || '',
          // by-company-base returns the sector NAME in `sector` (it maps
          // nama_sector), and carries no sector id — so sectorName is what the
          // read-only Sector field in LeadsForm needs, and `sector` (the id
          // this option shape expects) is genuinely unavailable here.
          sector: '',
          sectorName: clientData?.sector || '',
        },
        ...existing,
      ];
    }
    return merged;
  }, [options, presetClient, clientData]);

  // Every alert on this page is transient (3s). Centralised so the
  // setAlert + setTimeout pair can't drift apart between call sites.
  const showTransientAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: "", message: "" }), 3000);
  };

  // Fetch contacts for the company
  const fetchContacts = async (companyId) => {
    if (!companyId) return;
    try {
      const response = await api.get(`/crm/contacts/by-company/${companyId}`);
      setContacts(response.data || []);
    } catch (error) {
      setContacts([]);
    }
  };

  // Fetch client data
  useEffect(() => {
    const fetchClientData = async () => {
      try {
        setIsPageLoading(true);
        const response = await api.get(`/crm/leads/by-company-base/${id}`);
        setClientData(response.data);

        // Fetch contacts if companyId is available
        if (response.data?.companyId) {
          await fetchContacts(response.data.companyId);
        }
      } catch (error) {
      } finally {
        setIsPageLoading(false);
      }
    };
    fetchClientData();
  }, [id]);

  // ✅ FIX: Fetch tab data (leads, projects, etc.) - Only when activeTab changes AND clientData is ready
  useEffect(() => {
    // Rapidly switching tabs can let an older tab's response resolve AFTER a
    // newer tab is already active — commitTabData drops any response that
    // isn't from the most recently started request, so stale rows (with a
    // different shape, e.g. proposals' object-valued createdBy/assignedTo)
    // never get rendered against the wrong tab's columns.
    const requestId = ++tabDataRequestIdRef.current;
    const commitTabData = (data) => {
      if (tabDataRequestIdRef.current === requestId) {
        setTabData(data);
      }
    };

    const fetchData = async () => {
      // Skip if detail tab or clientData not ready
      if (activeTab === "detail" || !clientData?.companyId) {
        commitTabData([]); // Clear data for detail tab or when no companyId
        return;
      }

      setIsLoading(true);
      commitTabData([]); // Clear previous tab's rows immediately

      try {
        const companyId = clientData.companyId;

        if (activeTab === "leads") {
          const response = await api.get(
            `/crm/leads/by-company-id/${companyId}`,
          );
          commitTabData(response.data || []);
        } else if (activeTab === "proposals") {
          const response = await api.get(`/crm/proposals/company/${companyId}`);

          // Map proposal data with proper status transformations (same as Proposals component)
          let mappedProposals = (response.data || []).map((item) => ({
            ...item,
            proposal: item.proposalCode,
            client: item.companyName,
            amount: item.grandTotal,
            // Fix case sensitivity for status descriptions
            statusDesc:
              item.statusDesc === "APPROVED"
                ? "Approved"
                : item.statusDesc === "REJECTED"
                  ? "Rejected"
                  : item.statusDesc === "REVISION"
                    ? "Revision"
                    : item.statusDesc === "NEED_APPROVAL"
                      ? "Need Approval"
                      : item.statusDesc || "Draft",
            statusProposalDesc:
              item.statusProposalDesc === "ACCEPTED"
                ? "Accepted"
                : item.statusProposalDesc === "REJECTED"
                  ? "Rejected"
                  : item.statusProposalDesc === "FEEDBACK"
                    ? "Feedback"
                    : item.statusProposalDesc || "Draft",
            // Add project status and proposal stage fields
            // Default fallback - hanya bergantung pada data dari projects table
            projectStatus: "Not Converted", // Default, akan di-override oleh enrichment
            // Simpan ID stage agar bisa dilookup namanya dari tabel proposal_stages
            proposalStageId:
              item.proposalStageId ||
              item.proposalStage?.id ||
              item.stageId ||
              null,
            // Keep any backend-provided display name as fallback
            proposalStage: item.proposalStageName || item.proposalStage || null,
          }));

          // Enrich project status from projects.status_desc if available (same logic as Proposals component)
          try {
            const leadIds = Array.from(
              new Set(
                (mappedProposals || []).map((p) => p.leadId).filter(Boolean),
              ),
            );
            if (leadIds.length > 0) {
              try {
                // Try batch endpoint first (expects array of { leadId, status_desc })
                const batchRes = await api.get(
                  "/crm/projects/status-by-leads",
                  { params: { leadIds: leadIds.join(",") } },
                );
                const list = Array.isArray(batchRes?.data)
                  ? batchRes.data
                  : batchRes?.data
                    ? Object.values(batchRes.data)
                    : [];
                const statusMap = {};
                list.forEach((it) => {
                  const lid = it?.leadId ?? it?.lead_id ?? it?.lead;
                  const s = it?.status_desc ?? it?.statusDesc ?? it?.status;
                  if (lid != null && s != null) statusMap[lid] = s;
                });

                mappedProposals = mappedProposals.map((p) => {
                  const raw = statusMap[p.leadId];

                  const normalized = (() => {
                    if (raw == null) {
                      return null;
                    }
                    const val = String(raw).toLowerCase();
                    if (
                      [
                        "converted",
                        "1",
                        "true",
                        "done",
                        "closed",
                        "success",
                      ].includes(val)
                    ) {
                      return "Converted";
                    }
                    if (
                      [
                        "not converted",
                        "0",
                        "false",
                        "open",
                        "pending",
                        "draft",
                      ].includes(val)
                    ) {
                      return "Not Converted";
                    }
                    // default: use as given with first letter uppercased
                    return typeof raw === "string" ? raw : "Converted";
                  })();

                  return {
                    ...p,
                    projectStatus: normalized || p.projectStatus,
                  };
                });
              } catch (batchErr) {
                console.warn(
                  "Batch project status lookup failed, falling back per-lead:",
                  batchErr,
                );
                // Fallback to per-lead lookup (expects object with status_desc)
                const pairs = await Promise.all(
                  leadIds.map(async (lid) => {
                    try {
                      const r = await api.get(`/crm/projects/lead/${lid}`);
                      const s =
                        r?.data?.status_desc ??
                        r?.data?.statusDesc ??
                        r?.data?.status;
                      return [lid, s || (r?.data ? "Converted" : null)];
                    } catch (singleErr) {
                      return [lid, null];
                    }
                  }),
                );
                const map = Object.fromEntries(pairs);
                mappedProposals = mappedProposals.map((p) => {
                  const raw = map[p.leadId];
                  const normalized = (() => {
                    if (raw == null) return null;
                    const val = String(raw).toLowerCase();
                    if (
                      [
                        "converted",
                        "1",
                        "true",
                        "done",
                        "closed",
                        "success",
                      ].includes(val)
                    )
                      return "Converted";
                    if (
                      [
                        "not converted",
                        "0",
                        "false",
                        "open",
                        "pending",
                        "draft",
                      ].includes(val)
                    )
                      return "Not Converted";
                    return typeof raw === "string" ? raw : "Converted";
                  })();
                  return {
                    ...p,
                    projectStatus: normalized || p.projectStatus,
                  };
                });
              }
            }
          } catch (ignore) {
            // keep fallback projectStatus when enrichment fails
          }

          // Final safety: hanya bergantung pada data dari projects table, tidak ada fallback dari proposal
          mappedProposals = mappedProposals.map((p) => {
            // Jika tidak ada projectStatus dari enrichment, tetap 'Not Converted'
            if (!p.projectStatus) {
              return { ...p, projectStatus: "Not Converted" };
            }
            return p;
          });

          commitTabData(mappedProposals);
        } else if (activeTab === "projects") {
          const response = await api.get(`/crm/projects/company/${companyId}`);
          const mapped = (response.data || []).map((item) => ({
            ...item,
            amount: item.grandTotal,
            currencyCode: item.displayCurrencyCode || item.baseCurrencyCode || "IDR",
          }));
          commitTabData(mapped);
        } else if (activeTab === "invoices") {
          const response = await api.get(`/crm/invoices/company/${companyId}`);
          commitTabData(response.data || []);
        } else if (activeTab === "documents") {
          // ✅ Fetch both proposal documents AND project documents
          try {
            // Fetch proposal documents
            const proposalDocsResponse = await api.get(
              `/crm/proposals/company-documents/${companyId}`,
            );
            const proposalDocs = proposalDocsResponse.data || [];

            // Fetch all projects for this company
            const projectsResponse = await api.get(
              `/crm/projects/company/${companyId}`,
            );
            const projects = projectsResponse.data || [];

            // Fetch documents for each project
            const projectDocsPromises = projects.map(async (project) => {
              try {
                const docsResponse = await api.get(
                  `/crm/project-documents/project/${project.id}`,
                );
                const projectDocs = docsResponse.data || [];

                // Transform project documents to match table structure
                return projectDocs.map((doc) => ({
                  id: doc.id,
                  documentName: doc.originalFileName || doc.fileName,
                  type: "Project Document",
                  category: doc.documentType || "Project File",
                  source: "Project",
                  sourceCode: project.projectCode || `PROJ-${project.id}`,
                  uploadedDate: doc.createdAt,
                  uploadedBy: doc.uploadedBy,
                  size: doc.fileSize,
                  fileType: doc.fileType || doc.mimeType,
                  // Keep original data for download
                  originalData: doc,
                  projectId: project.id,
                  projectCode: project.projectCode,
                }));
              } catch (error) {
                return [];
              }
            });

            const projectDocsArrays = await Promise.all(projectDocsPromises);
            const projectDocs = projectDocsArrays.flat();

            // Combine proposal and project documents
            const allDocuments = [...proposalDocs, ...projectDocs];

            commitTabData(allDocuments);
          } catch (error) {
            commitTabData([]);
          }
        } else if (activeTab === "tasks") {
          const response = await api.get(
            `/crm/lead-tasks/by-company/${companyId}`,
          );
          commitTabData(response.data || []);
        } else if (activeTab === "payments") {
          const response = await api.get(`/crm/payments/company/${companyId}`);
          commitTabData(response.data || []);
        } else if (activeTab === "expenses") {
          const response = await api.get(`/crm/expenses/company/${companyId}`);
          commitTabData(response.data || []);
        } else {
          commitTabData([]);
        }
      } catch (error) {
        commitTabData([]);
      } finally {
        if (tabDataRequestIdRef.current === requestId) {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, [activeTab, clientData?.companyId]); // ✅ FIX: Include clientData?.companyId dependency

  // Release the body scroll lock when the lead modal closes.
  // NOTE: never remove .modal-backdrop here — LeadsForm renders its backdrop
  // through ReactDOM.createPortal, so detaching that node makes React's own
  // unmount throw NotFoundError on removeChild. See utils/modalUtils.js.
  useEffect(() => {
    if (isModalOpen) return;
    document.body.classList.remove("modal-open");
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
  }, [isModalOpen]);

  const formatAmount = (amount, currencyCode = "IDR") => {
    if (!amount || amount === 0) {
      return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: currencyCode,
        minimumFractionDigits: 0,
      }).format(0);
    }
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Matches formatDateDisplay in src/pages/Leads/index.jsx — keep both in sync
  const formatDateDisplay = (dateValue) => {
    if (!dateValue) return "";

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  // Matches formatInvoiceIssuedLabel in src/pages/Invoice/index.jsx — keep both in sync
  function formatInvoiceIssuedLabel(value) {
    if (!value) return "";
    if (value === "before") return "Before";
    if (value === "after") return "After";
    return value;
  }

  // Preview uploaded PDF for an invoice row (same behavior as src/pages/Invoice/index.jsx)
  const handlePreviewUploadedInvoicePdf = async (row) => {
    try {
      if (!row?.id) return;
      const response = await api.get(`/crm/invoices/${row.id}/uploaded-pdf`, {
        responseType: "blob",
      });

      const contentType = response.headers?.["content-type"] || "";
      if (contentType.includes("application/json")) {
        const text = (await response.data.text?.()) || "";
        let message = "Failed to preview uploaded PDF.";
        try {
          const json = JSON.parse(text);
          message = json.error || json.message || message;
        } catch (_) {}
        setAlert({ show: true, type: "warning", message });
        return;
      }

      const blob = new Blob([response.data], {
        type: contentType || "application/pdf",
      });
      const url = window.URL.createObjectURL(blob);

      setPdfTitle(`Invoice PDF - ${row.invoiceNumber || row.id}`);
      setPdfUrl(url);
      setShowPdfModal(true);
    } catch (e) {
      setAlert({
        show: true,
        type: "danger",
        message: "Failed to preview uploaded PDF.",
      });
    }
  };

  const handlePdfModalClose = () => {
    setShowPdfModal(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    setPdfTitle("");
  };

  // Matches calculateRemainingYears in src/pages/Project/index.jsx — keep both in sync
  const calculateRemainingYears = (projectSectionsOrProjectServices) => {
    if (!projectSectionsOrProjectServices || !Array.isArray(projectSectionsOrProjectServices)) return 0;
    const currentYear = new Date().getFullYear();
    let maxYear = currentYear;

    const collectMaxYearFromStandards = (standards) => {
      if (!standards || !Array.isArray(standards)) return;
      standards.forEach((s) => {
        if (s.selectedYear != null && typeof s.selectedYear === "number" && s.selectedYear > maxYear) {
          maxYear = s.selectedYear;
        }
        if (s.proposeDate) {
          const proposeYear = new Date(s.proposeDate).getFullYear();
          if (!Number.isNaN(proposeYear) && proposeYear > maxYear) maxYear = proposeYear;
        }
        if (s.children && Array.isArray(s.children)) collectMaxYearFromStandards(s.children);
      });
    };

    const hasStandards = projectSectionsOrProjectServices.some(
      (item) => item.standards && Array.isArray(item.standards),
    );
    if (hasStandards) {
      projectSectionsOrProjectServices.forEach((ps) => {
        if (ps.standards && Array.isArray(ps.standards)) collectMaxYearFromStandards(ps.standards);
      });
    } else {
      projectSectionsOrProjectServices.forEach((section) => {
        const services = section.services || [];
        services.forEach((service) => {
          if (service.selectedYear != null && typeof service.selectedYear === "number" && service.selectedYear > maxYear) {
            maxYear = service.selectedYear;
          }
          const sub = service.sub_services || [];
          sub.forEach((subService) => {
            if (subService.selectedYear != null && typeof subService.selectedYear === "number" && subService.selectedYear > maxYear) {
              maxYear = subService.selectedYear;
            }
          });
        });
      });
    }

    return Math.max(1, maxYear - currentYear + 1);
  };

  // Matches yearsLeftFromEndDate in src/pages/Project/index.jsx — keep both in sync
  const yearsLeftFromEndDate = (validUntil) => {
    if (!validUntil) return 1;
    const end = new Date(validUntil);
    if (Number.isNaN(end.getTime())) return 1;
    const currentYear = new Date().getFullYear();
    const endYear = end.getFullYear();
    return Math.max(1, endYear - currentYear + 1);
  };

  // ✅ TASK UPDATE PERMISSION: Memoize columns to recalculate when permission changes
  // Table columns config
  const columns = useMemo(
    () => ({
      leads: [
        {
          key: "projectName",
          label: "Project Name",
          render: (value, row) => (
            <a
              href={`/leads/detail/${row.id}`}
              className="text-primary text-truncate d-inline-block text-decoration-none"
              style={{ cursor: "pointer", maxWidth: "200px" }}
              onClick={(e) => {
                // Allow right-click, Ctrl+Click, Cmd+Click to open in new tab
                if (e.ctrlKey || e.metaKey || e.button === 1) {
                  return; // Let browser handle it
                }
                e.preventDefault();
                e.stopPropagation();
                handleNavigateToLeadDetail(row);
              }}
              onAuxClick={(e) => {
                // Handle middle mouse button click
                if (e.button === 1) {
                  return; // Let browser handle it
                }
              }}
              title={value}
              aria-label={`View lead details for ${value}`}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleNavigateToLeadDetail(row);
                }
              }}
            >
              {value || "-"}
            </a>
          ),
        },
        {
          key: "tagging",
          label: "Tagging",
          render: (value) => renderBadgeList(value, "bg-blue-lt"),
        },
        {
          key: "sector",
          label: "Sector",
          render: (value, row) => value || row.sector?.name || "-",
        },
        {
          key: "service",
          label: "Service",
          render: (value, row) => renderBadgeList(row.serviceNames, "bg-green-lt"),
        },
        {
          key: "standard",
          label: "Standard",
          render: (value, row) => renderBadgeList(row.standardNames, "bg-azure-lt"),
        },
        { key: "partnerType", label: "Partner Type" },
        {
          key: "branch",
          label: "Branch",
          render: (value, row) => row.branchName || "N/A",
        },
        {
          key: "createdBy",
          label: "Created By",
          render: (value, row) =>
            `${row.createdBy?.firstName || ""} ${row.createdBy?.lastName || ""}`.trim() ||
            "-",
        },
        {
          key: "proposalStatus",
          label: "Proposal Status",
          render: (value, row) => {
            const label = row.hasProposal ? "Converted" : "Not Converted";
            return <span className={getProposalStatusBadge(label)}>{label}</span>;
          },
        },
        { key: "pic", label: "PIC" },
        { key: "email", label: "Email" },
        { key: "contactNumber", label: "Phone" },
        { key: "assignUser", label: "Assigned To" },
        {
          key: "department",
          label: "Department",
          render: (value) => value?.name || "-",
        },
        { key: "stage", label: "Stage" },
        {
          key: "status",
          label: "Leads Status",
          render: (value) => (
            <span
              className={`badge badge-outline text-${value === "Converted" ? "success" : "orange"}`}
            >
              {value || "Not Converted"}
            </span>
          ),
        },
        {
          key: "leadSource",
          label: "Lead Source",
          render: (value) => value || "-",
        },
        {
          key: "createdAt",
          label: "Created At",
          type: "date",
          render: (value) => formatDateDisplay(value) || "-",
        },
        {
          key: "updatedAt",
          label: "Updated At",
          type: "date",
          render: (value) => formatDateDisplay(value) || "-",
        },
        {
          key: "taskUpdateDate",
          label: "Task Update",
          type: "date",
          render: (value) => formatDateDisplay(value) || "-",
        },
        {
          key: "taskUpdateBy",
          label: "Task Update By",
          render: (value) =>
            value
              ? `${value.firstName || ""} ${value.lastName || ""}`.trim() || "-"
              : "-",
        },
      ],
      projects: [
        {
          key: "projectCode",
          label: "Project",
          render: (value, row) => (
            <a
              href={`/projects/detail/${row.leadId}`}
              className="text-primary text-truncate d-inline-block text-decoration-none"
              style={{ cursor: "pointer", maxWidth: "150px" }}
              onClick={(e) => {
                // Allow right-click, Ctrl+Click, Cmd+Click to open in new tab
                if (e.ctrlKey || e.metaKey || e.button === 1) {
                  return; // Let browser handle it
                }
                e.preventDefault();
                e.stopPropagation();
                handleNavigateToProjectDetail(row);
              }}
              onAuxClick={(e) => {
                // Handle middle mouse button click
                if (e.button === 1) {
                  return; // Let browser handle it
                }
              }}
              title={value}
              aria-label={`View project details for ${value}`}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleNavigateToProjectDetail(row);
                }
              }}
            >
              {value || "-"}
            </a>
          ),
        },
        {
          key: "tagging",
          label: "Tagging",
          render: (value, row) => {
            const raw = String(row.tagging ?? "");
            const tags = raw
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
            if (tags.length === 0) return <span className="text-muted">-</span>;
            return (
              <span
                className="d-inline-flex flex-wrap gap-1"
                style={{ maxWidth: "180px" }}
              >
                {tags.map((tag, i) => (
                  <span
                    key={i}
                    className="badge bg-blue-lt text-wrap"
                    title={tag}
                    style={{ fontSize: "0.7rem" }}
                  >
                    {tag}
                  </span>
                ))}
              </span>
            );
          },
        },
        {
          key: "projectName",
          label: "Project Name",
          render: (value) => value || "-",
        },
        {
          key: "service",
          label: "Service",
          render: (value, row) => {
            const services = String(row.serviceNames || "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
            if (services.length === 0) return <span className="text-muted">-</span>;
            return (
              <span
                className="d-inline-flex flex-wrap gap-1"
                style={{ maxWidth: "180px" }}
              >
                {services.map((svc, i) => (
                  <span
                    key={i}
                    className="badge bg-green-lt text-wrap"
                    title={svc}
                    style={{ fontSize: "0.7rem" }}
                  >
                    {svc}
                  </span>
                ))}
              </span>
            );
          },
        },
        {
          key: "standard",
          label: "Standard",
          render: (value, row) => {
            const standards = String(row.standardNames || "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
            if (standards.length === 0) return <span className="text-muted">-</span>;
            return (
              <span
                className="d-inline-flex flex-wrap gap-1"
                style={{ maxWidth: "180px" }}
              >
                {standards.map((std, i) => (
                  <span
                    key={i}
                    className="badge bg-azure-lt text-wrap"
                    title={std}
                    style={{ fontSize: "0.7rem" }}
                  >
                    {std}
                  </span>
                ))}
              </span>
            );
          },
        },
        {
          key: "branch",
          label: "Branch",
          render: (value, row) => row.branchName || "N/A",
        },
        {
          key: "partnerType",
          label: "Partner Type",
        },
        {
          key: "projectDate",
          label: "Project Start Date",
          type: "date",
          render: (value) => formatDateDisplay(value) || "-",
        },
        {
          key: "validUntil",
          label: "Project End Date",
          type: "date",
          render: (value) => formatDateDisplay(value) || "-",
        },
        {
          key: "yearLeft",
          label: "Year Left",
          render: (value, row) => {
            const sectionsOrServices = row.project_sections || row.projectServices;
            const hasServices =
              Array.isArray(sectionsOrServices) && sectionsOrServices.length > 0;
            const years = hasServices
              ? calculateRemainingYears(sectionsOrServices)
              : yearsLeftFromEndDate(row.validUntil);
            return `${years} year${years !== 1 ? "s" : ""} left`;
          },
        },
        {
          key: "departmentName",
          label: "Department",
          render: (value, row) =>
            row.departmentName || row.department?.name || "N/A",
        },
        {
          key: "assignedTo",
          label: "Assigned To",
          render: (value, row) =>
            row.assignedToUser?.fullName ||
            (row.assignedToUser?.firstName && row.assignedToUser?.lastName
              ? `${row.assignedToUser.firstName} ${row.assignedToUser.lastName}`.trim()
              : null) ||
            (typeof row.assignedTo === "string" ? row.assignedTo : null) ||
            "N/A",
        },
        {
          key: "amount",
          label: "Amount",
          render: (value, row) =>
            formatAmount(
              value,
              row?.displayCurrencyCode || row?.currencyCode || "IDR",
            ),
        },
        {
          key: "auditDates",
          label: "Audit Date",
          render: (value, row) => {
            const dates = row.proposeDates || [];
            if (dates.length === 0) return <span className="text-muted">-</span>;
            return dates
              .map((d) => {
                const parts = String(d).split("-");
                return parts.length === 3
                  ? `${parts[2]}/${parts[1]}/${parts[0]}`
                  : d;
              })
              .join(", ");
          },
        },
        {
          key: "createdAt",
          label: "Created Date",
          type: "date",
          render: (value) => formatDateDisplay(value) || "-",
        },
      ],
      proposals: [
        {
          key: "proposal",
          label: "Proposal",
          render: (value, row) => (
            <a
              href={`/proposals/detail/${row.leadId}`}
              className="text-primary text-truncate d-inline-block text-decoration-none"
              style={{ cursor: "pointer", maxWidth: "150px" }}
              onClick={(e) => {
                // Allow right-click, Ctrl+Click, Cmd+Click to open in new tab
                if (e.ctrlKey || e.metaKey || e.button === 1) {
                  return; // Let browser handle it
                }
                e.preventDefault();
                e.stopPropagation();
                handleNavigateToProposalDetail(row);
              }}
              onAuxClick={(e) => {
                // Handle middle mouse button click
                if (e.button === 1) {
                  return; // Let browser handle it
                }
              }}
              title={value}
              aria-label={`View proposal details for ${value}`}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleNavigateToProposalDetail(row);
                }
              }}
            >
              {value || "-"}
            </a>
          ),
        },
        {
          key: "tagging",
          label: "Tagging",
          render: (value, row) => {
            const companyTags = String(row.tagging || "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
            const proposalTags = String(row.proposalTagging || "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
            if (companyTags.length === 0 && proposalTags.length === 0) {
              return <span className="text-muted">-</span>;
            }
            return (
              <span
                className="d-inline-flex flex-wrap gap-1"
                style={{ maxWidth: "200px" }}
              >
                {companyTags.map((tag, i) => (
                  <span
                    key={`c-${i}`}
                    className="badge bg-blue-lt text-wrap"
                    title={`Company tag: ${tag}`}
                    style={{ fontSize: "0.7rem" }}
                  >
                    {tag}
                  </span>
                ))}
                {proposalTags.map((tag, i) => (
                  <span
                    key={`p-${i}`}
                    className="badge bg-yellow-lt text-wrap"
                    title={`Proposal tag: ${tag}`}
                    style={{ fontSize: "0.7rem" }}
                  >
                    {tag}
                  </span>
                ))}
              </span>
            );
          },
        },
        {
          key: "proposalDate",
          label: "Proposal Date",
          type: "date",
          render: (value) => formatDateDisplay(value) || "-",
        },
        {
          key: "validUntil",
          label: "Valid Until",
          type: "date",
          render: (value) => formatDateDisplay(value) || "-",
        },
        {
          key: "partnerType",
          label: "Partner Type",
        },
        {
          key: "sector",
          label: "Sector",
          render: (value, row) => row.sectorName || row.sector || "-",
        },
        {
          key: "service",
          label: "Service",
          render: (value, row) => {
            const services = String(row.serviceNames || "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
            if (services.length === 0) return <span className="text-muted">-</span>;
            return (
              <span
                className="d-inline-flex flex-wrap gap-1"
                style={{ maxWidth: "180px" }}
              >
                {services.map((svc, i) => (
                  <span
                    key={i}
                    className="badge bg-green-lt text-wrap"
                    title={svc}
                    style={{ fontSize: "0.7rem" }}
                  >
                    {svc}
                  </span>
                ))}
              </span>
            );
          },
        },
        {
          key: "standard",
          label: "Standard",
          render: (value, row) => {
            const standards = String(row.standards || "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
            if (standards.length === 0) return <span className="text-muted">-</span>;
            return (
              <span
                className="d-inline-flex flex-wrap gap-1"
                style={{ maxWidth: "180px" }}
              >
                {standards.map((std, i) => (
                  <span
                    key={i}
                    className="badge bg-azure-lt text-wrap"
                    title={std}
                    style={{ fontSize: "0.7rem" }}
                  >
                    {std}
                  </span>
                ))}
              </span>
            );
          },
        },
        {
          key: "amount",
          label: "Amount",
          render: (value, row) =>
            formatAmount(
              value,
              row?.displayCurrencyCode || row?.currencyCode || "IDR",
            ),
        },
        {
          key: "createdBy",
          label: "Created By",
          render: (value, row) =>
            `${row.createdBy?.firstName || ""} ${row.createdBy?.lastName || ""}`.trim() ||
            "-",
        },
        {
          key: "branch",
          label: "Branch",
          render: (value, row) => row.branchName || "N/A",
        },
        {
          key: "department",
          label: "Department",
          render: (value, row) => row.department?.name || "N/A",
        },
        {
          key: "assignedTo",
          label: "Assigned To",
          render: (value, row) =>
            `${row.assignedTo?.firstName || ""} ${row.assignedTo?.lastName || ""}`.trim() ||
            "-",
        },
        {
          key: "approver",
          label: "Approver",
          render: (value, row) =>
            row.statusDesc === "Need Approval" && row.approver
              ? `${row.approver?.firstName || ""} ${row.approver?.lastName || ""}`.trim() ||
                "-"
              : "-",
        },
        {
          key: "createdAt",
          label: "Created At",
          type: "date",
          render: (value) => formatDateDisplay(value) || "-",
        },
        {
          key: "updatedAt",
          label: "Updated At",
          type: "date",
          render: (value) => formatDateDisplay(value) || "-",
        },
        {
          key: "statusDesc",
          label: "Status Approval",
          render: (value) => (
            <span className={getStatusBadge(value)}>{value || "Draft"}</span>
          ),
        },
        {
          key: "statusProposalDesc",
          label: "Status Proposal",
          render: (value, row) => {
            // Use statusProposalDesc that we properly mapped from the API
            const status = row.statusProposalDesc || "Draft";
            return (
              <span className={getProposalStatusBadge(status)}>{status}</span>
            );
          },
        },
        {
          key: "projectStatus",
          label: "Project Status",
          render: (value, row) => {
            // Use projectStatus that we properly enriched from the projects API
            const isConverted = row.projectStatus === "Converted";
            return (
              <span className={getProjectStatusBadge(isConverted)}>
                {isConverted ? "Converted" : "Not Converted"}
              </span>
            );
          },
        },
        {
          key: "proposalStage",
          label: "Proposal Stage",
          render: (value, row) => row.proposalStage || row.proposalStageName || "-",
        },
        {
          key: "stageUpdatedAt",
          label: "Stage Updated",
          type: "date",
          render: (value) => formatDateDisplay(value) || "-",
        },
      ],
      invoices: [
        {
          key: "uploadedPdf",
          label: "PDF",
          render: (value, row) => (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handlePreviewUploadedInvoicePdf(row);
              }}
              title={
                row?.documentPath
                  ? "Download Uploaded PDF"
                  : "Try download (will warn if not uploaded)"
              }
            >
              <FileText className="icon-tabler icon-tabler-download" width="18" height="18" />
            </a>
          ),
        },
        {
          key: "invoiceNumber",
          label: "Invoice Number",
          render: (value, row) => (
            <a
              href={`/invoice/${row.id}`}
              className="text-primary text-truncate d-inline-block text-decoration-none"
              style={{ cursor: "pointer", maxWidth: "120px", width: "120px" }}
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey || e.button === 1) return;
                e.preventDefault();
                e.stopPropagation();
                navigate(`/invoice/${row.id}`);
              }}
              onAuxClick={(e) => {
                if (e.button === 1) return;
              }}
              title={value}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(`/invoice/${row.id}`);
                }
              }}
            >
              {value || "-"}
            </a>
          ),
        },
        {
          key: "jurnalNumber",
          label: "Jurnal Number",
          render: (value) => (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: "150px", width: "150px" }}
              title={value}
            >
              {value || "-"}
            </span>
          ),
        },
        {
          key: "departmentName",
          label: "Department",
          render: (value) => (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: "150px", width: "150px" }}
              title={value}
            >
              {value || "-"}
            </span>
          ),
        },
        {
          key: "client",
          label: "Client",
          render: (value) => (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: "200px", width: "200px" }}
              title={value}
            >
              {value || "-"}
            </span>
          ),
        },
        {
          key: "tagging",
          label: "Tagging",
          render: (value) => {
            const tags = value
              ? String(value)
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
              : [];
            if (tags.length === 0) return <span className="text-muted">-</span>;
            return (
              <span
                className="d-inline-flex flex-wrap gap-1"
                style={{ maxWidth: "180px" }}
              >
                {tags.map((tag, i) => (
                  <span
                    key={i}
                    className="badge bg-blue-lt text-wrap"
                    title={tag}
                    style={{ fontSize: "0.7rem" }}
                  >
                    {tag}
                  </span>
                ))}
              </span>
            );
          },
        },
        {
          key: "description",
          label: "Description",
          render: (value) => (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: "250px", width: "250px" }}
              title={value}
            >
              {value || "-"}
            </span>
          ),
        },
        {
          key: "projectName",
          label: "Project",
          render: (value) => (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: "200px", width: "200px" }}
              title={value}
            >
              {value || "-"}
            </span>
          ),
        },
        {
          key: "invoiceTo",
          label: "Invoice To",
          render: (value) => (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: "180px", width: "180px" }}
              title={value}
            >
              {value || "-"}
            </span>
          ),
        },
        {
          key: "invoiceIssued",
          label: "Term of Payments",
          render: (value) => {
            const displayValue = formatInvoiceIssuedLabel(value);
            return (
              <span
                className="text-truncate d-inline-block"
                style={{ maxWidth: "140px", width: "140px" }}
                title={displayValue}
              >
                {displayValue || "-"}
              </span>
            );
          },
        },
        {
          key: "proposalCode",
          label: "Order Number",
          render: (value, row) => {
            const v = row?.proposalCode || row?.proposal_code || "-";
            return (
              <span
                className="text-truncate d-inline-block"
                style={{ maxWidth: "200px", width: "200px" }}
                title={v}
              >
                {v}
              </span>
            );
          },
        },
        {
          key: "subtotal",
          label: "Sub Total",
          render: (value, row) => {
            const v = formatAmount(
              value,
              row?.displayCurrencyCode || row?.currencyCode || "IDR",
            );
            return (
              <span
                className="text-truncate d-inline-block"
                style={{ maxWidth: "150px", width: "150px" }}
                title={v}
              >
                {v}
              </span>
            );
          },
        },
        {
          key: "ppn",
          label: "PPN",
          render: (value, row) => {
            const v = formatAmount(
              value,
              row?.displayCurrencyCode || row?.currencyCode || "IDR",
            );
            return (
              <span
                className="text-truncate d-inline-block"
                style={{ maxWidth: "150px", width: "150px" }}
                title={v}
              >
                {v}
              </span>
            );
          },
        },
        {
          key: "totalInvoiced",
          label: "Total Invoiced",
          render: (value, row) => {
            const v = formatAmount(
              value,
              row?.displayCurrencyCode || row?.currencyCode || "IDR",
            );
            return (
              <span
                className="text-truncate d-inline-block"
                style={{ maxWidth: "150px", width: "150px" }}
                title={v}
              >
                {v}
              </span>
            );
          },
        },
        {
          key: "paymentReceived",
          label: "Payment Received",
          render: (value, row) => {
            const v = formatAmount(
              value,
              row?.displayCurrencyCode || row?.currencyCode || "IDR",
            );
            return (
              <span
                className="text-truncate d-inline-block"
                style={{ maxWidth: "150px", width: "150px" }}
                title={v}
              >
                {v}
              </span>
            );
          },
        },
        {
          key: "due",
          label: "Due",
          render: (value, row) => {
            const v = formatAmount(
              value,
              row?.displayCurrencyCode || row?.currencyCode || "IDR",
            );
            return (
              <span
                className="text-truncate d-inline-block"
                style={{ maxWidth: "150px", width: "150px" }}
                title={v}
              >
                {v}
              </span>
            );
          },
        },
        {
          key: "balance",
          label: "Balance",
          render: (value, row) => {
            const v = formatAmount(
              value,
              row?.displayCurrencyCode || row?.currencyCode || "IDR",
            );
            return (
              <span
                className="text-truncate d-inline-block"
                style={{ maxWidth: "150px", width: "150px" }}
                title={v}
              >
                {v}
              </span>
            );
          },
        },
        {
          key: "billDate",
          label: "Bill Date",
          type: "date",
          render: (value) => {
            const v = formatDateDisplay(value) || "-";
            return (
              <span
                className="text-truncate d-inline-block"
                style={{ maxWidth: "140px", width: "140px" }}
                title={v}
              >
                {v}
              </span>
            );
          },
        },
        {
          key: "dueDate",
          label: "Due Date",
          type: "date",
          render: (value) => {
            const v = formatDateDisplay(value) || "-";
            return (
              <span
                className="text-truncate d-inline-block"
                style={{ maxWidth: "140px", width: "140px" }}
                title={v}
              >
                {v}
              </span>
            );
          },
        },
        {
          key: "assignedUserName",
          label: "Assigned To",
          render: (value) => (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: "160px", width: "160px" }}
              title={value}
            >
              {value || "-"}
            </span>
          ),
        },
        {
          key: "createdByName",
          label: "Created By",
          render: (value) => (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: "160px", width: "160px" }}
              title={value}
            >
              {value || "-"}
            </span>
          ),
        },
        {
          key: "updatedByName",
          label: "Updated By",
          render: (value) => (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: "160px", width: "160px" }}
              title={value}
            >
              {value || "-"}
            </span>
          ),
        },
        {
          key: "serviceName",
          label: "Service",
          render: (value) => (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: "150px", width: "150px" }}
              title={value}
            >
              {value || "-"}
            </span>
          ),
        },
        {
          key: "status",
          label: "Status",
          render: (value) => {
            const statusText =
              value === "SENT" ? "SENT TO FINANCE" : value || "Draft";
            return (
              <span
                className={`${getInvoiceStatusBadge(value)} text-truncate d-inline-block`}
                style={{ maxWidth: "150px", width: "150px" }}
                title={statusText}
              >
                {statusText}
              </span>
            );
          },
        },
      ],
      documents: [
        {
          key: "documentName",
          label: "Document Name",
          render: (value) => (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: "200px" }}
              title={value}
            >
              {value || "-"}
            </span>
          ),
        },
        { key: "type", label: "Type" },
        { key: "category", label: "Category" },
        { key: "source", label: "Source" },
        { key: "sourceCode", label: "Reference" },
        {
          key: "uploadedDate",
          label: "Uploaded",
          type: "date",
          render: (value) =>
            value ? new Date(value).toLocaleDateString("id-ID") : "-",
        },
        {
          key: "uploadedBy",
          label: "Uploaded By",
          render: (value) => value || "-",
        },
        {
          key: "size",
          label: "Size",
          render: (value) => (value ? formatFileSize(value) : "-"),
        },
        { key: "fileType", label: "File Type" },
        {
          key: "download",
          label: "Download",
          render: (value, row) => (
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleDownloadDocument(row)}
              title={`Download ${row.documentName}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7,10 12,15 17,10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
            </button>
          ),
        },
      ],
      tasks: [
        {
          key: "title",
          label: "Task Title",
          render: (value) => value || "-",
        },
        {
          key: "contactName",
          label: "Contact Name",
          render: (value) => value || "-",
        },
        {
          key: "projectName",
          label: "Project Name",
          render: (value) => value || "-",
        },
        {
          key: "assignedUser",
          label: "Assigned To",
          render: (value) => value || "-",
        },
        {
          key: "type",
          label: "Type",
          render: (value) => value || "-",
        },
        {
          key: "priority",
          label: "Priority",
          render: (value) => {
            if (!value) return "-";
            const colors = {
              high: "bg-danger text-white",
              medium: "bg-warning text-white",
              low: "bg-info text-white",
            };
            const str = String(value);
            const displayValue =
              str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
            return (
              <span
                className={`badge ${colors[str.toLowerCase()] || "badge-outline"}`}
              >
                {displayValue}
              </span>
            );
          },
        },
        {
          key: "status",
          label: "Status",
          render: (value) => {
            if (!value) return "-";
            const colors = {
              done: "badge-outline text-success",
              "to do": "badge-outline text-primary",
              "in progress": "badge-outline text-warning",
            };
            const str = String(value);
            const displayValue =
              str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
            return (
              <span
                className={`badge ${colors[str.toLowerCase()] || "badge-outline"}`}
              >
                {displayValue}
              </span>
            );
          },
        },
        {
          key: "description",
          label: "Description",
          render: (value) => (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: "250px" }}
              title={value}
            >
              {value || "-"}
            </span>
          ),
        },
        {
          key: "fileName",
          label: "File",
          render: (value) => (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: "150px" }}
              title={value}
            >
              {value || "-"}
            </span>
          ),
        },
        {
          key: "tagging",
          label: "Tagging",
          // Company-level tagging: identical on every row inside this page, so
          // it is defined for parity with the Task list but hidden by default.
          render: () => renderBadgeList(clientData?.tagging, "bg-blue-lt"),
        },
        {
          key: "taskUpdateBy",
          label: "Task Update By",
          render: (value) => value || "-",
        },
        {
          key: "dueDate",
          label: "Due Date",
          type: "date",
          render: (value) =>
            value ? new Date(value).toLocaleDateString("id-ID") : "-",
        },
        {
          key: "createdAt",
          label: "Created At",
          type: "date",
          render: (value) =>
            value ? new Date(value).toLocaleDateString("id-ID") : "-",
        },
        // ✅ TASK UPDATE PERMISSION: Only show Updated At column if user has permission
        ...(canViewTaskUpdate
          ? [
              {
                key: "updatedAt",
                label: "Updated At",
                type: "date",
                render: (value) =>
                  value ? new Date(value).toLocaleDateString("id-ID") : "-",
              },
            ]
          : []),
      ],
      expenses: [
        {
          key: "tagging",
          label: "Tagging",
          render: () => renderBadgeList(clientData?.tagging, "bg-blue-lt"),
        },
        {
          key: "expenseDate",
          label: "Date",
          render: (value) => {
            if (!value) return "-";
            const date = new Date(value);
            return `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
          },
        },
        {
          key: "projectName",
          label: "Project Name",
          render: (value) => (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: "200px" }}
              title={value}
            >
              {value || "-"}
            </span>
          ),
        },
        {
          key: "categoryName",
          label: "Category",
          render: (value) => (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: "150px" }}
              title={value}
            >
              {value || "-"}
            </span>
          ),
        },
        {
          key: "description",
          label: "Description",
          render: (value) => (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: "250px" }}
              title={value}
            >
              {value || "-"}
            </span>
          ),
        },
        {
          key: "fileName",
          label: "File",
          render: (value, row) => {
            if (!value) return "-";
            return (
              <button
                type="button"
                className="btn btn-link text-primary text-decoration-none p-0 border-0"
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  try {
                    const response = await api.get(
                      `/crm/expenses/download/${row.id}`,
                      { responseType: "blob" },
                    );
                    let originalFileName = value;
                    if (originalFileName && originalFileName.includes("_")) {
                      const idx = originalFileName.lastIndexOf("_");
                      if (idx > 0)
                        originalFileName = originalFileName.substring(idx + 1);
                    }
                    const url = window.URL.createObjectURL(
                      new Blob([response.data]),
                    );
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = originalFileName;
                    link.target = "_blank";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                  } catch (error) {
                    setAlert({
                      show: true,
                      type: "danger",
                      message: "Failed to download file. Please try again.",
                    });
                    setTimeout(
                      () => setAlert({ show: false, type: "", message: "" }),
                      3000,
                    );
                  }
                }}
              >
                View
              </button>
            );
          },
        },
        {
          key: "amount",
          label: "Amount",
          render: (value, row) => {
            if (!value) return "-";
            const currencyCode = row.currencyCode || "IDR";
            const locale =
              currencyCode === "USD"
                ? "en-US"
                : currencyCode === "EUR"
                  ? "de-DE"
                  : "id-ID";
            return new Intl.NumberFormat(locale, {
              style: "currency",
              currency: currencyCode,
              minimumFractionDigits: 0,
            }).format(parseFloat(value));
          },
        },
      ],
      payments: [
        {
          key: "id",
          label: "Payment ID",
          render: (value) => `PAY-${String(value).padStart(4, "0")}`,
        },
        {
          key: "tagging",
          label: "Tagging",
          render: () => renderBadgeList(clientData?.tagging, "bg-blue-lt"),
        },
        {
          key: "invoiceNumber",
          label: "Invoice Number",
          render: (value) => value || "-",
        },
        {
          key: "paymentMethod",
          label: "Payment Method",
          render: (value) => value || "-",
        },
        {
          key: "paymentDate",
          label: "Payment Date",
          render: (value) => {
            if (!value) return "-";
            const d = new Date(value);
            if (Number.isNaN(d.getTime())) return value;
            return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
          },
        },
        {
          key: "amount",
          label: "Amount",
          render: (value, row) => {
            const currency = row?.currencyCode || "IDR";
            const locale = currency === "USD" ? "en-US" : currency === "EUR" ? "de-DE" : "id-ID";
            return value
              ? new Intl.NumberFormat(locale, {
                  style: "currency",
                  currency,
                  minimumFractionDigits: 0,
                }).format(value)
              : new Intl.NumberFormat(locale, { style: "currency", currency, minimumFractionDigits: 0 }).format(0);
          },
        },
        { key: "note", label: "Note", render: (value) => value || "-" },
        {
          key: "createdBy",
          label: "Created By",
          render: (value) => value || "-",
        },
        {
          key: "createdAt",
          label: "Created At",
          render: (value) =>
            value ? new Date(value).toLocaleDateString("id-ID") : "-",
        },
        {
          key: "updatedBy",
          label: "Updated By",
          render: (value) => value || "-",
        },
        {
          key: "updatedAt",
          label: "Updated At",
          render: (value) =>
            value ? new Date(value).toLocaleDateString("id-ID") : "-",
        },
        {
          key: "documentName",
          label: "File",
          render: (value) =>
            value ? (
              <a
                href={`/files/${value}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                  <path d="M14 3v4a1 1 0 0 0 1 1h4"></path>
                  <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z"></path>
                </svg>
              </a>
            ) : (
              "-"
            ),
        },
      ],
    }),
    [canViewTaskUpdate],
  );

  // Handlers for filter, search, export, etc.
  // Export exactly what the user is looking at: the visible columns of the
  // currently filtered/searched rows, in the on-screen column order.
  const buildExportTable = () => {
    const tabColumns = columns[activeTab] || [];
    const visible = visibleColumns[activeTab] || [];
    const selected = tabColumns.filter((col) => visible.includes(col.key));
    const rows = getFilteredTabData();
    return {
      headers: selected.map((col) => col.label),
      // Raw values only — a column's render() returns JSX, which cannot be
      // serialised into a spreadsheet or a PDF cell.
      body: rows.map((row) => selected.map((col) => stringifyCell(row[col.key]))),
      rowCount: rows.length,
    };
  };

  const exportBaseName = () =>
    `${(companyName || decodedCompanyName || "client").replace(/[^\w-]+/g, "_")}_${activeTab}_${new Date().toISOString().slice(0, 10)}`;

  const handleExportExcel = () => {
    const { headers, body, rowCount } = buildExportTable();
    if (rowCount === 0) {
      showTransientAlert("warning", `No ${activeTab} to export.`);
      return;
    }
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...body]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, capitalize(activeTab));
    XLSX.writeFile(workbook, `${exportBaseName()}.xlsx`);
  };

  const handleExportPDF = () => {
    const { headers, body, rowCount } = buildExportTable();
    if (rowCount === 0) {
      showTransientAlert("warning", `No ${activeTab} to export.`);
      return;
    }
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    doc.setFontSize(12);
    doc.text(
      `${companyName || decodedCompanyName} — ${capitalize(activeTab)}`,
      14,
      14,
    );
    autoTable(doc, {
      head: [headers],
      body,
      startY: 20,
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [31, 101, 108] },
    });
    doc.save(`${exportBaseName()}.pdf`);
  };

  const getStatusBadge = (status) => {
    const colors = {
      1: "warning",
      2: "info",
      3: "primary",
      4: "danger",
      5: "secondary",
      Accepted: "success",
      Rejected: "danger",
      Feedback: "warning",
      Draft: "secondary",
      "Need Approval": "primary",
      Pending: "warning",
      Approved: "success",
      Converted: "success",
      "Not Converted": "secondary",
    };
    return `badge badge-outline text-${colors[status] || "secondary"}`;
  };

  const getProposalStatusBadge = (status) => {
    const colors = {
      Draft: "secondary",
      Sent: "info",
      Viewed: "primary",
      Accepted: "success",
      Rejected: "danger",
      Expired: "warning",
    };
    return `badge badge-outline text-${colors[status] || "secondary"}`;
  };

  const getProjectStatusBadge = (isConverted) => {
    return isConverted
      ? "badge badge-outline text-success"
      : "badge badge-outline text-secondary";
  };

  const getInvoiceStatusBadge = (status) => {
    const colors = {
      PAID: "bg-success text-white",
      UNPAID: "bg-warning text-white",
      OVERDUE: "bg-danger text-white",
      PARTIAL: "bg-info text-white",
      DRAFT: "badge-outline text-orange",
      SENT: "badge-outline text-blue",
      CANCELLED: "badge-outline text-gray",
      FINAL: "bg-primary text-white",
    };
    return `badge ${colors[status] || "badge-outline text-gray"}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleDownloadDocument = async (doc) => {
    try {
      // ✅ Special handling for Project documents (use blob download)
      if (doc.source === "Project") {
        try {
          const response = await api.get(
            `/crm/project-documents/download/${doc.id}`,
            {
              responseType: "blob",
            },
          );

          // Create blob download
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", doc.documentName || "file");
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);

          setAlert({
            show: true,
            type: "success",
            message: `Download started: ${doc.documentName}`,
          });
          setTimeout(() => {
            setAlert({ show: false, type: "", message: "" });
          }, 3000);
          return;
        } catch (error) {
          throw error;
        }
      }

      // For other documents, check if filePath exists
      if (!doc.filePath) {
        setAlert({
          show: true,
          type: "warning",
          message: "File path not available for download",
        });
        setTimeout(() => {
          setAlert({ show: false, type: "", message: "" });
        }, 3000);
        return;
      }

      let downloadUrl;

      // Check if it's a Lead document (CID1, CID2, Other Files), Lead Task, or Payment - use direct URL
      if (
        (doc.source === "Lead" &&
          (doc.category === "CID1" ||
            doc.category === "CID2" ||
            doc.category === "Other Files")) ||
        doc.source === "Lead Task" ||
        doc.source === "Payment"
      ) {
        downloadUrl = doc.filePath; // Already contains full URL
      } else {
        // For other documents (Proposal, Invoice), use ProposalController endpoint
        downloadUrl = `/api/crm/proposals/download-document?path=${encodeURIComponent(doc.filePath)}`;
      }

      // Create temporary link and trigger download
      const linkElement = document.createElement("a");
      linkElement.href = downloadUrl;
      linkElement.download = doc.documentName || "document";
      linkElement.target = "_blank";

      // Append to body, click, and remove
      document.body.appendChild(linkElement);
      linkElement.click();
      document.body.removeChild(linkElement);

      setAlert({
        show: true,
        type: "success",
        message: `Download started: ${doc.documentName}`,
      });
      setTimeout(() => {
        setAlert({ show: false, type: "", message: "" });
      }, 3000);
    } catch (error) {
      let errorMessage = `Failed to download ${doc.documentName}. Please try again.`;

      if (error.response?.status === 404) {
        errorMessage = `File not found: ${doc.documentName}. The file may have been moved or deleted.`;
      } else if (error.response?.status === 403) {
        errorMessage = `Access denied: ${doc.documentName}. You may not have permission to download this file.`;
      } else if (error.response?.status >= 500) {
        errorMessage = `Server error while downloading ${doc.documentName}. Please try again later.`;
      }

      setAlert({
        show: true,
        type: "danger",
        message: errorMessage,
      });
      setTimeout(() => {
        setAlert({ show: false, type: "", message: "" });
      }, 5000); // Show error longer for debugging
    }
  };

  const handleTabChange = (tabId, e) => {
    e.preventDefault();
    setActiveTab(tabId);
  };

  const handleBackClick = (e) => {
    e.preventDefault();
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/company');
    }
  };

  // Navigation handlers for detail pages
  const handleNavigateToLeadDetail = (lead) => {
    navigate(`/leads/detail/${lead.id}`);
  };

  const handleNavigateToProjectDetail = (project) => {
    navigate(`/projects/detail/${project.leadId}`);
  };

  const handleNavigateToProposalDetail = (proposal) => {
    navigate(`/proposals/detail/${proposal.leadId}`);
  };

  const handleNavigateToInvoiceDetail = (invoice) => {
    navigate(`/invoice/${invoice.id}`);
  };

  // Row click handler based on active tab
  const handleRowClick = (item) => {
    if (activeTab === "leads") {
      handleNavigateToLeadDetail(item);
    } else if (activeTab === "projects") {
      handleNavigateToProjectDetail(item);
    } else if (activeTab === "proposals") {
      handleNavigateToProposalDetail(item);
    } else if (activeTab === "invoices") {
      handleNavigateToInvoiceDetail(item);
    }
  };

  const deleteLeadById = async (id) => {
    try {
      const response = await api.delete(`/crm/leads/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Tabs whose rows can actually be deleted from here. A tab absent from this
  // map gets no delete button at all — previously every tab rendered one and the
  // ones with no branch below closed the modal without issuing any request.
  const DELETABLE_TABS = {
    leads: { label: "lead", remove: (row) => deleteLeadById(row.id) },
    tasks: { label: "task", remove: (row) => deleteTask(row.id) },
    invoices: {
      label: "invoice",
      remove: (row) => api.delete(`/crm/invoices/${row.id}`),
    },
    payments: {
      label: "payment",
      remove: (row) => api.delete(`/crm/payments/${row.id}`),
    },
    expenses: { label: "expense", remove: (row) => deleteExpense(row.id) },
  };

  const deletableTab = DELETABLE_TABS[activeTab];

  const canDeleteActiveTab = () => {
    if (!deletableTab) return false;
    if (activeTab === "leads") return canLeads("delete");
    if (activeTab === "tasks") return canTask("delete");
    if (activeTab === "invoices") return canInvoice("delete");
    return true; // payments / expenses: no capability gate on their own pages either
  };

  const handleDelete = (item) => {
    if (!canDeleteActiveTab()) {
      showTransientAlert(
        "danger",
        `You do not have permission to delete ${deletableTab?.label ?? "this record"}s.`,
      );
      return;
    }
    setDeleteTarget(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !deletableTab) return;
    const { label, remove } = deletableTab;

    try {
      await remove(deleteTarget);
      setTabData((prev) => prev.filter((row) => row.id !== deleteTarget.id));
      showTransientAlert("success", `${capitalize(label)} deleted successfully`);
    } catch (error) {
      showTransientAlert(
        "danger",
        `Failed to delete ${label}. Please try again.`,
      );
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const handleEditLead = (lead) => {
    setIsEditMode(true);
    setEditingLead(lead);
    resetForm();

    // Minimal delay to ensure form reset is complete
    setTimeout(() => {
      setIsModalOpen(true);
    }, 100); // Reduced from 300ms to 100ms
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditingLead(null);
    resetForm();
    // Body scroll lock is released by the isModalOpen effect above; the backdrop
    // node itself belongs to React and must not be removed by hand.
  };

  const handleAddNew = () => {
    if (activeTab === "leads") {
      setIsEditMode(false);
      setEditingLead(null);
      resetForm();

      // Pre-populate form with current company data
      setTimeout(() => {
        if (clientData) {
          // Pre-fill company information FIRST before setting company
          // This ensures fields are populated even if company is not found in options
          handleInputChange("companyWebsite", clientData.website || "");
          handleInputChange(
            "companyPhoneNumber",
            clientData.contactNumber || clientData.phone || "",
          );
          handleInputChange("address", clientData.address || "");
          handleInputChange(
            "sector",
            clientData.partnerType || clientData.sectorName || "",
          );

          // Set company ID AFTER setting company details
          // This ensures company details are already set when company is selected
          if (clientData.companyId) {
            handleInputChange("company", String(clientData.companyId));

            // Fetch contacts for the selected company
            fetchContactOptions(String(clientData.companyId));
          }

          // Set other fields
          handleInputChange("stage", "New");
          handleInputChange("projectName", "");
          handleInputChange("projectDescription", "");
          handleInputChange("leadSource", "");
        }

        // Open modal after data is set (moved here to match Prospects pattern)
        setIsModalOpen(true);
      }, 150); // Reduced from 300ms to 150ms
    } else if (activeTab === "tasks") {
      setSelectedTask(null);
      setIsTaskModalOpen(true);
    }
  };

  const handleAddNewTask = () => {
    setSelectedTask(null);
    setIsTaskModalOpen(true);
  };

  const buildEmptyPaymentForm = () => ({
    client: clientData?.companyId ? String(clientData.companyId) : '',
    invoice: '',
    paymentMethod: '',
    paymentDate: new Date().toISOString().split('T')[0],
    amount: '',
    note: '',
    file: null,
  });

  const handleAddNewPayment = () => {
    setIsPaymentEditMode(false);
    setEditingPayment(null);
    setPaymentFormData(buildEmptyPaymentForm());
    setPaymentFormErrors({});
    setIsPaymentModalOpen(true);
  };

  const handleEditPayment = (payment) => {
    setIsPaymentEditMode(true);
    setEditingPayment(payment);
    // Same field mapping as src/pages/Payment/index.jsx handleEditPayment.
    setPaymentFormData({
      client: payment.clientId ? String(payment.clientId) : buildEmptyPaymentForm().client,
      invoice: payment.invoiceId ? String(payment.invoiceId) : '',
      paymentMethod: payment.paymentMethod || '',
      paymentDate: payment.paymentDate || '',
      amount: payment.amount != null ? String(payment.amount) : '',
      note: payment.note || '',
      file: payment.documentName || null,
    });
    setPaymentFormErrors({});
    setIsPaymentModalOpen(true);
  };

  // Currency inputs hand back strings like "Rp 1.500.000" — strip to a number.
  const parsePaymentAmount = (value) => {
    if (typeof value === "string") {
      return parseFloat(value.replace(/[^\d.-]/g, ""));
    }
    return parseFloat(value);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    const companyId = clientData?.companyId;
    if (!companyId) {
      showTransientAlert("danger", "Client is not loaded yet. Please retry.");
      return;
    }

    // Same checks as src/pages/Payment/index.jsx — notably the numeric amount
    // check, without which a non-numeric entry posts the string "NaN".
    const errors = {};
    if (!paymentFormData.client) errors.client = "Client is required";
    if (!paymentFormData.invoice) errors.invoice = "Invoice is required";
    if (!paymentFormData.paymentMethod)
      errors.paymentMethod = "Payment method is required";
    if (!paymentFormData.paymentDate)
      errors.paymentDate = "Payment date is required";
    if (!paymentFormData.amount) {
      errors.amount = "Amount is required";
    } else {
      const numericAmount = parsePaymentAmount(paymentFormData.amount);
      if (Number.isNaN(numericAmount) || numericAmount <= 0) {
        errors.amount = "Amount must be greater than 0";
      }
    }
    if (Object.keys(errors).length > 0) {
      setPaymentFormErrors(errors);
      return;
    }

    const numericAmount = parsePaymentAmount(paymentFormData.amount);

    try {
      if (isPaymentEditMode && editingPayment?.id) {
        // Update takes JSON, not multipart — matching PUT /crm/payments/{id}
        // and src/pages/Payment/index.jsx. The document is not replaceable here.
        await api.put(`/crm/payments/${editingPayment.id}`, {
          clientId: paymentFormData.client || companyId,
          invoiceId: paymentFormData.invoice,
          paymentMethod: paymentFormData.paymentMethod,
          paymentDate: paymentFormData.paymentDate,
          amount: numericAmount,
          note: paymentFormData.note || "",
        });
      } else {
        const data = new FormData();
        data.append("clientId", companyId);
        data.append("invoiceId", paymentFormData.invoice);
        data.append("paymentMethod", paymentFormData.paymentMethod);
        data.append("paymentDate", paymentFormData.paymentDate);
        data.append("amount", numericAmount);
        data.append("note", paymentFormData.note || "");
        if (paymentFormData.file instanceof File) {
          data.append("document", paymentFormData.file);
        }
        await api.post("/crm/payments", data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      setIsPaymentModalOpen(false);
      showTransientAlert(
        "success",
        isPaymentEditMode
          ? "Payment updated successfully!"
          : "Payment created successfully!",
      );
      setIsPaymentEditMode(false);
      setEditingPayment(null);
      await refreshPayments();
    } catch (error) {
      showTransientAlert(
        "danger",
        error.response?.data?.message ||
          `Failed to ${isPaymentEditMode ? "update" : "create"} payment.`,
      );
    }
  };

  const refreshPayments = async () => {
    const companyId = clientData?.companyId;
    if (!companyId) return;
    const res = await api.get(`/crm/payments/company/${companyId}`);
    setTabData(res.data || []);
  };

  const handleAddNewInvoice = () => {
    setIsInvoiceEditMode(false);
    setEditingInvoice(null);
    setInvoiceFormData(buildEmptyInvoiceForm(clientData?.companyId));
    setInvoiceFormErrors({});
    setIsInvoiceModalOpen(true);
  };

  const handleEditInvoice = async (invoice) => {
    try {
      const fullInvoiceData = await getInvoice(invoice.id);
      setIsInvoiceEditMode(true);
      setEditingInvoice(fullInvoiceData);
      setInvoiceFormData({
        client: fullInvoiceData.companyId || fullInvoiceData.client || '',
        projectName: fullInvoiceData.projectId || fullInvoiceData.projectName || '',
        projectId: fullInvoiceData.projectId || null,
        leadId: fullInvoiceData.leadId || null,
        billDate: fullInvoiceData.billDate || new Date().toISOString().split('T')[0],
        dueDate: fullInvoiceData.dueDate || '',
        description: fullInvoiceData.description || '',
        jurnalNumber: fullInvoiceData.jurnalNumber || '',
        invoiceTo: fullInvoiceData.invoiceTo || '',
        invoiceIssued: fullInvoiceData.invoiceIssued || '',
        tax: fullInvoiceData.taxPercentage || fullInvoiceData.tax || 0,
        applyTax: (fullInvoiceData.taxPercentage || fullInvoiceData.tax || 0) > 0,
        documents: fullInvoiceData.documentPath ? [{
          name: fullInvoiceData.documentPath.split('/').pop(),
          url: fullInvoiceData.documentPath,
          uploaded: true
        }] : [],
        status: fullInvoiceData.status || 'Draft',
        items: fullInvoiceData.items || [],
        subtotal: fullInvoiceData.subtotal || 0,
        dppLainnya: fullInvoiceData.dppLainnya || 0,
        total: fullInvoiceData.total || 0,
        amountReceived: fullInvoiceData.amountReceived || 0,
        message: fullInvoiceData.message || '',
        notes: fullInvoiceData.notes || '',
        terms: fullInvoiceData.terms || '',
        paymentMethod: fullInvoiceData.paymentMethod || '',
        bankAccount: fullInvoiceData.bankAccount || '',
      });
      setInvoiceFormErrors({});
      setIsInvoiceModalOpen(true);
    } catch (error) {
      setAlert({
        show: true,
        type: 'danger',
        message: 'Failed to load invoice details. Please try again.',
      });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
    }
  };

  const handleInvoiceInputChange = (name, value) => {
    if (name === 'tax') {
      const taxValue = typeof value === 'string' ? parseFloat(value) || 0 : (value || 0);
      setInvoiceFormData(prev => ({ ...prev, tax: taxValue, applyTax: taxValue > 0 }));
    } else {
      setInvoiceFormData(prev => ({ ...prev, [name]: value }));
    }
    if (invoiceFormErrors[name]) {
      setInvoiceFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleInvoiceFieldBlur = (name, value) => {
    const errors = validateInvoiceForm({ [name]: value, isEditMode: isInvoiceEditMode });
    if (errors[name]) {
      setInvoiceFormErrors(prev => ({ ...prev, [name]: errors[name] }));
    }
  };

  const handleInvoiceFormSubmit = async (e, onSuccess, formDataFromChild = null) => {
    e.preventDefault();
    if (isInvoiceEditMode ? !canInvoice("edit") : !canInvoice("create")) {
      showTransientAlert(
        "danger",
        `You do not have permission to ${isInvoiceEditMode ? "edit" : "create"} invoices.`,
      );
      return;
    }
    setIsInvoiceSubmitting(true);
    const dataToValidate = formDataFromChild || invoiceFormData;
    const errors = validateInvoiceForm({ ...dataToValidate, isEditMode: isInvoiceEditMode });
    if (Object.keys(errors).length > 0) {
      setInvoiceFormErrors(errors);
      setIsInvoiceSubmitting(false);
      return;
    }
    try {
      setInvoiceFormErrors({});
      const projectIdToSend = isInvoiceEditMode
        ? (dataToValidate.projectId || dataToValidate.projectName || editingInvoice?.projectId)
        : (dataToValidate.projectName || dataToValidate.projectId);
      const payload = {
        companyId: dataToValidate.client,
        projectId: projectIdToSend,
        billDate: dataToValidate.billDate,
        dueDate: dataToValidate.dueDate,
        description: dataToValidate.description,
        jurnalNumber: dataToValidate.jurnalNumber || '',
        invoiceTo: dataToValidate.invoiceTo || '',
        invoiceIssued: dataToValidate.invoiceIssued || '',
        taxPercentage: dataToValidate.tax || 0,
        applyTax: dataToValidate.applyTax || false,
        subtotal: dataToValidate.subtotal || 0,
        dppLainnya: dataToValidate.dppLainnya || 0,
        amountReceived: dataToValidate.amountReceived || 0,
        message: dataToValidate.message || '',
        notes: dataToValidate.notes || '',
        terms: dataToValidate.terms || '',
        paymentMethod: dataToValidate.paymentMethod || '',
        bankAccount: dataToValidate.bankAccount || '',
      };
      if (!isInvoiceEditMode) {
        payload.status = 'DRAFT';
      }
      const file = dataToValidate.documents?.[0]?.file;

      // Reject oversized files client-side; the server otherwise answers with an
      // opaque MULTIPART_ERROR. Same guard as src/pages/Invoice/index.jsx.
      if (file && file.size > MAX_INVOICE_FILE_SIZE_MB * 1024 * 1024) {
        setAlert({
          show: true,
          type: 'danger',
          message: `File terlalu besar (${(file.size / 1024 / 1024).toFixed(1)} MB). Maksimal ukuran file adalah ${MAX_INVOICE_FILE_SIZE_MB} MB. Harap kompres PDF terlebih dahulu (gunakan ilovepdf.com atau sejenisnya).`,
        });
        setIsInvoiceSubmitting(false);
        return;
      }

      let response;
      if (isInvoiceEditMode) {
        if (file) {
          const fd = new FormData();
          fd.append('invoice', new Blob([JSON.stringify(payload)], { type: 'application/json' }), 'invoice.json');
          fd.append('file', file, file.name);
          response = await api.put(`/crm/invoices/${editingInvoice.id}`, fd, {
            maxBodyLength: Infinity,
            maxContentLength: Infinity,
          });
        } else {
          response = await api.put(`/crm/invoices/${editingInvoice.id}`, payload);
        }
      } else {
        // POST /crm/invoices only declares a MULTIPART_FORM_DATA mapping — a plain
        // JSON body is rejected with 415, so always send multipart even with no file.
        const fd = new FormData();
        fd.append('invoice', new Blob([JSON.stringify(payload)], { type: 'application/json' }), 'invoice.json');
        if (file) fd.append('file', file, file.name);
        response = await api.post('/crm/invoices', fd, {
          maxBodyLength: Infinity,
          maxContentLength: Infinity,
        });
      }
      if (response.data.success) {
        setAlert({ show: true, type: 'success', message: isInvoiceEditMode ? 'Invoice updated successfully!' : 'Invoice created successfully!' });
        if (clientData?.companyId) {
          const res = await api.get(`/crm/invoices/company/${clientData.companyId}`);
          setTabData(res.data || []);
        }
        if (onSuccess) onSuccess();
        setIsInvoiceModalOpen(false);
        setIsInvoiceEditMode(false);
        setEditingInvoice(null);
      } else {
        setAlert({ show: true, type: 'danger', message: response.data.error || `Failed to ${isInvoiceEditMode ? 'update' : 'create'} invoice.` });
      }
    } catch (error) {
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || `Failed to ${isInvoiceEditMode ? 'update' : 'create'} invoice.`;
      setAlert({ show: true, type: 'danger', message: msg });
    } finally {
      setIsInvoiceSubmitting(false);
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
    }
  };

  const handleAddNewExpense = () => {
    setSelectedExpense(null);
    setIsExpenseModalOpen(true);
  };

  const handleEditExpense = (expense) => {
    setSelectedExpense(expense);
    setIsExpenseModalOpen(true);
  };

  const handleExpenseSubmit = async (formData) => {
    try {
      if (selectedExpense) {
        await updateExpense(selectedExpense.id, formData);
        setAlert({ show: true, type: "success", message: "Expense updated successfully" });
      } else {
        await createExpense(formData);
        setAlert({ show: true, type: "success", message: "Expense created successfully" });
      }
      if (clientData?.companyId) {
        const res = await api.get(`/crm/expenses/company/${clientData.companyId}`);
        setTabData(res.data || []);
      }
      setIsExpenseModalOpen(false);
      setSelectedExpense(null);
      setTimeout(() => setAlert({ show: false, type: "", message: "" }), 3000);
    } catch (error) {
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || "Failed to save expense";
      setAlert({ show: true, type: "danger", message: msg });
      setTimeout(() => setAlert({ show: false, type: "", message: "" }), 3000);
      throw error;
    }
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setIsTaskModalOpen(true);
  };

  const handleTaskSubmit = async (taskData) => {
    if (selectedTask ? !canTask("edit") : !canTask("create")) {
      showTransientAlert(
        "danger",
        `You do not have permission to ${selectedTask ? "edit" : "create"} tasks.`,
      );
      return;
    }
    try {
      if (selectedTask) {
        await updateTask(selectedTask.id, taskData);
        setAlert({
          show: true,
          type: "success",
          message: "Task updated successfully",
        });
      } else {
        await createTask(taskData);
        setAlert({
          show: true,
          type: "success",
          message: "Task created successfully",
        });
      }

      // Refresh tasks data
      if (clientData?.companyId) {
        const response = await api.get(
          `/crm/lead-tasks/by-company/${clientData.companyId}`,
        );
        setTabData(response.data || []);
      }

      setIsTaskModalOpen(false);
      setSelectedTask(null);

      setTimeout(() => {
        setAlert({ show: false, type: "", message: "" });
      }, 3000);
    } catch (error) {
      setAlert({
        show: true,
        type: "danger",
        message: "Failed to save task. Please try again.",
      });
      setTimeout(() => {
        setAlert({ show: false, type: "", message: "" });
      }, 3000);
    }
  };

  // Handle column visibility changes for the current tab
  // Edit affordance per tab, each gated by the same capability its own page uses.
  // Returning undefined hides the button entirely rather than showing a dead one.
  const resolveRowEditHandler = () => {
    if (activeTab === "leads") return canLeads("edit") ? handleEditLead : undefined;
    if (activeTab === "tasks") return canTask("edit") ? handleEditTask : undefined;
    if (activeTab === "invoices")
      return canInvoice("edit") ? handleEditInvoice : undefined;
    if (activeTab === "expenses") return handleEditExpense;
    if (activeTab === "payments") return handleEditPayment;
    return undefined;
  };

  const handleVisibleColumnsChange = (newVisibleColumns) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [activeTab]: newVisibleColumns,
    }));
  };

  // Fields searched per tab. Previously only leads/tasks were searchable while
  // the search box rendered on every tab, so typing did nothing elsewhere.
  const SEARCHABLE_FIELDS = {
    leads: [
      "projectName",
      "stage",
      "pic",
      "email",
      "contactNumber",
      "assignUser",
      "status",
    ],
    tasks: [
      "taskTitle",
      "title",
      "contactName",
      "projectName",
      "assignedUser",
      "type",
      "status",
      "priority",
    ],
    proposals: [
      "proposal",
      "proposalCode",
      "client",
      "serviceNames",
      "statusDesc",
      "statusProposalDesc",
    ],
    projects: [
      "projectCode",
      "projectName",
      "serviceNames",
      "standardNames",
      "branchName",
      "departmentName",
    ],
    invoices: [
      "invoiceNumber",
      "jurnalNumber",
      "client",
      "description",
      "projectName",
      "invoiceTo",
      "proposalCode",
      "status",
    ],
    documents: [
      "documentName",
      "type",
      "category",
      "source",
      "sourceCode",
      "uploadedBy",
    ],
    payments: ["invoiceNumber", "paymentMethod", "note", "createdBy"],
    expenses: ["projectName", "categoryName", "description", "fileName"],
  };

  const matchesSearch = (row, query) => {
    const fields = SEARCHABLE_FIELDS[activeTab];
    if (!fields) return true;
    return fields.some((field) => {
      const value = row?.[field];
      return value != null && String(value).toLowerCase().includes(query);
    });
  };

  const getFilteredTabData = () => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return tabData;
    return tabData.filter((row) => matchesSearch(row, query));
  };

  // Refetch client data after company update (e.g. from detail edit)
  const handleClientUpdated = async () => {
    if (!id) return;
    try {
      const response = await api.get(`/crm/leads/by-company-base/${id}`);
      setClientData(response.data);
      if (response.data?.companyId) {
        await fetchContacts(response.data.companyId);
      }
    } catch (err) {
      console.error("Error refetching client after update:", err);
    }
  };

  // Render tab content
  const renderTabContent = () => {
    if (activeTab === "detail") {
      return (
        <Detail
          clientData={clientData}
          contacts={contacts}
          onUpdateClient={handleClientUpdated}
        />
      );
    }
    const placeholder = `Search ${activeTab}...`;
    return (
      <>
        <GenericView
          data={getFilteredTabData()}
          columns={columns[activeTab]}
          onDelete={deletableTab && canDeleteActiveTab() ? handleDelete : undefined}
          onEdit={resolveRowEditHandler()}
          searchComponent={
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={placeholder}
            />
          }
          actionButtons={
            <div className="d-flex gap-2">
              {activeTab === "leads" && canLeads("create") && (
                <button className="btn btn-primary" onClick={handleAddNew}>
                  Add New Lead
                </button>
              )}
              {activeTab === "tasks" && canTask("create") && (
                <button className="btn btn-primary" onClick={handleAddNewTask}>
                  Add New Task
                </button>
              )}
              {activeTab === "invoices" && canInvoice("create") && (
                <button className="btn btn-primary" onClick={handleAddNewInvoice}>
                  Add New Invoice
                </button>
              )}
              {activeTab === "payments" && (
                <button className="btn btn-primary" onClick={handleAddNewPayment}>
                  Add New Payment
                </button>
              )}
              {activeTab === "expenses" && (
                <button className="btn btn-primary" onClick={handleAddNewExpense}>
                  Add New Expense
                </button>
              )}
              <ActionButtons
                onExportExcel={handleExportExcel}
                onExportPDF={handleExportPDF}
              />
            </div>
          }
          checkboxSelection={true}
          onRowClick={handleRowClick}
          emptyStateMessage={`No ${activeTab} found`}
          isLoading={isLoading}
          uniqueId={`clients-${activeTab}`}
          visibleColumns={visibleColumns[activeTab]}
          onVisibleColumnsChange={handleVisibleColumnsChange}
        />
        {isModalOpen && (
          <LeadsForm
            isOpen={isModalOpen}
            onClose={handleModalClose}
            formData={formData}
            formErrors={formErrors}
            handleInputChange={handleInputChange}
            handleTextChange={handleTextChange}
            handleFormSubmit={handleFormSubmit}
            serviceSections={serviceSections}
            addServiceSection={addServiceSection}
            deleteServiceSection={deleteServiceSection}
            options={allOptions}
            fetchContactOptions={fetchContactOptions}
            standardsOptionsMap={standardsOptionsMap}
            uploadedFiles={uploadedFiles}
            onDrop={onDrop}
            removeFile={removeFile}
            isEditMode={isEditMode}
            editingLead={editingLead}
            initializeServiceSections={initializeServiceSections}
            isSubmitting={isLeadSubmitting}
            lockCompany={true}
            // LeadsForm calls this with no arguments and only on success, so
            // there is no error case to branch on here — a failed save surfaces
            // through useLeadsForm's own form errors.
            onSuccessSubmit={() => {
              handleModalClose();
              showTransientAlert(
                "success",
                isEditMode
                  ? "Lead updated successfully!"
                  : "Lead created successfully!",
              );

              const companyId = clientData?.companyId;
              if (companyId) {
                api
                  .get(`/crm/leads/by-company-id/${companyId}`)
                  .then((response) => setTabData(response.data || []))
                  .catch(() =>
                    showTransientAlert(
                      "danger",
                      "Failed to refresh leads data. Please try again.",
                    ),
                  );
              }
            }}
          />
        )}
        {isInvoiceModalOpen && (
          <InvoiceForm
            isOpen={isInvoiceModalOpen}
            onClose={() => {
              setIsInvoiceModalOpen(false);
              setIsInvoiceEditMode(false);
              setEditingInvoice(null);
            }}
            formData={invoiceFormData}
            formErrors={invoiceFormErrors}
            handleInputChange={handleInvoiceInputChange}
            handleFieldBlur={handleInvoiceFieldBlur}
            handleFormSubmit={handleInvoiceFormSubmit}
            isEditMode={isInvoiceEditMode}
            editingInvoice={editingInvoice}
            isSubmitting={isInvoiceSubmitting}
            presetClient={presetClient}
            lockClient
          />
        )}
        {isPaymentModalOpen && (
          <PaymentForm
            isOpen={isPaymentModalOpen}
            onClose={() => {
              setIsPaymentModalOpen(false);
              setPaymentFormErrors({});
              setIsPaymentEditMode(false);
              setEditingPayment(null);
            }}
            formData={paymentFormData}
            formErrors={paymentFormErrors}
            handleInputChange={(name, value) => setPaymentFormData(prev => ({ ...prev, [name]: value }))}
            handleFormSubmit={handlePaymentSubmit}
            isEditMode={isPaymentEditMode}
            editingPayment={editingPayment}
            disableClientSelect={true}
            presetClient={presetClient}
            refreshPayments={refreshPayments}
          />
        )}
        {isExpenseModalOpen && (
          <ExpenseForm
            isOpen={isExpenseModalOpen}
            onClose={() => { setIsExpenseModalOpen(false); setSelectedExpense(null); }}
            onSubmit={handleExpenseSubmit}
            initialData={selectedExpense || null}
            disabledClientId={clientData?.companyId ? String(clientData.companyId) : undefined}
            presetClient={presetClient}
          />
        )}
        {isTaskModalOpen && (
          <TaskForm
            isOpen={isTaskModalOpen}
            onClose={() => {
              setIsTaskModalOpen(false);
              setSelectedTask(null);
            }}
            onSubmit={handleTaskSubmit}
            // Create must pass initialData={null}: a truthy initialData puts
            // TaskForm in its edit branch, which titles the modal "Edit Task"
            // and skips the view_own auto-assign. The company is preselected
            // through defaultClientId instead.
            initialData={
              selectedTask
                ? {
                    ...selectedTask,
                    clientId: selectedTask.companyId ?? clientData?.companyId,
                    taskTitle: selectedTask.title,
                  }
                : null
            }
            defaultClientId={clientData?.companyId}
            disableCompanyField={true} // Disable company field since we're in company context
            hasViewOwnPermission={canTask("view_own")}
            hasViewGlobalPermission={canTask("view_global")}
          />
        )}
      </>
    );
  };

  // Show loading while page data is being fetched
  if (isPageLoading) {
    return <Loading text="Loading client details..." />;
  }

  return (
    <div className="container-xl">
      {alert.show && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert({ show: false, type: "", message: "" })}
        />
      )}
      <div className="page-header d-print-none">
        <div className="row align-items-center">
          <div className="col">
            <div className="d-flex align-items-center">
              <a
                className="btn btn-outline-white d-none d-sm-inline-block me-3"
                onClick={handleBackClick}
              >
                <ArrowLeft className="icon icon-tabler icon-tabler-arrow-left" />
                Back
              </a>
              <h2 className="page-title mb-0">{companyName || "Loading..."}</h2>
            </div>
          </div>
        </div>
      </div>
      <div className="page-body">
        <div className="container-xl">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs">
                  <li className="nav-item-tab" role="presentation">
                    <a
                      href="#"
                      className={`nav-link ${activeTab === "detail" ? "active" : ""}`}
                      onClick={(e) => handleTabChange("detail", e)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="icon icon-tabler icon-tabler-building me-2"
                      >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M3 21l18 0" />
                        <path d="M9 8l1 0" />
                        <path d="M9 12l1 0" />
                        <path d="M9 16l1 0" />
                        <path d="M14 8l1 0" />
                        <path d="M14 12l1 0" />
                        <path d="M14 16l1 0" />
                        <path d="M5 21v-16a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v16" />
                      </svg>
                      Detail
                    </a>
                  </li>

                  <li className="nav-item-tab" role="presentation">
                    <a
                      href="#"
                      className={`nav-link ${activeTab === "leads" ? "active" : ""}`}
                      onClick={(e) => handleTabChange("leads", e)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="icon icon-tabler icon-tabler-file me-2"
                      >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                        <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
                      </svg>
                      Leads
                    </a>
                  </li>

                  <li className="nav-item-tab" role="presentation">
                    <a
                      href="#"
                      className={`nav-link ${activeTab === "proposals" ? "active" : ""}`}
                      onClick={(e) => handleTabChange("proposals", e)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="icon icon-tabler icon-tabler-file-description me-2"
                      >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                        <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
                        <path d="M9 17h6" />
                        <path d="M9 13h6" />
                      </svg>
                      Proposals
                    </a>
                  </li>

                  <li className="nav-item-tab" role="presentation">
                    <a
                      href="#"
                      className={`nav-link ${activeTab === "projects" ? "active" : ""}`}
                      onClick={(e) => handleTabChange("projects", e)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="icon icon-tabler icon-tabler-calendar-event me-2"
                      >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M4 5m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z" />
                        <path d="M16 3l0 4" />
                        <path d="M8 3l0 4" />
                        <path d="M4 11l16 0" />
                        <path d="M8 15h2v2h-2z" />
                      </svg>
                      Projects
                    </a>
                  </li>

                  <li className="nav-item-tab" role="presentation">
                    <a
                      href="#"
                      className={`nav-link ${activeTab === "tasks" ? "active" : ""}`}
                      onClick={(e) => handleTabChange("tasks", e)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="icon icon-tabler icon-tabler-checklist me-2"
                      >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M9.615 20h-2.615a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v8" />
                        <path d="M14 19l2 2l4 -4" />
                        <path d="M9 8h4" />
                        <path d="M9 12h2" />
                      </svg>
                      Tasks
                    </a>
                  </li>

                  <li className="nav-item-tab" role="presentation">
                    <a
                      href="#"
                      className={`nav-link ${activeTab === "invoices" ? "active" : ""}`}
                      onClick={(e) => handleTabChange("invoices", e)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-invoice me-2"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M19 12v7a1.78 1.78 0 0 1 -3.1 1.4a1.65 1.65 0 0 0 -2.6 0a1.65 1.65 0 0 1 -2.6 0a1.65 1.65 0 0 0 -2.6 0a1.78 1.78 0 0 1 -3.1 -1.4v-14a2 2 0 0 1 2 -2h7l5 5v4.25" /></svg>
                      Invoices
                    </a>
                  </li>

                  <li className="nav-item-tab" role="presentation">
                    <a
                      href="#"
                      className={`nav-link ${activeTab === "payments" ? "active" : ""}`}
                      onClick={(e) => handleTabChange("payments", e)}
                    ><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-files me-2"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M15 3v4a1 1 0 0 0 1 1h4" /><path d="M18 17h-7a2 2 0 0 1 -2 -2v-10a2 2 0 0 1 2 -2h4l5 5v7a2 2 0 0 1 -2 2z" /><path d="M16 17v2a2 2 0 0 1 -2 2h-7a2 2 0 0 1 -2 -2v-10a2 2 0 0 1 2 -2h2" /></svg>
                      Payments
                    </a>
                  </li>

                  <li className="nav-item-tab" role="presentation">
                    <a
                      href="#"
                      className={`nav-link ${activeTab === "expenses" ? "active" : ""}`}
                      onClick={(e) => handleTabChange("expenses", e)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-receipt me-2"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M5 21v-16a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v16l-3 -2l-2 2l-2 -2l-2 2l-2 -2l-3 2" /><path d="M14 8h-2.5a1.5 1.5 0 0 0 0 3h1a1.5 1.5 0 0 1 0 3h-2.5m2 0v1.5m0 -9v1.5" /></svg>
                      Expenses
                    </a>
                  </li>

                  <li className="nav-item-tab" role="presentation">
                    <a
                      href="#"
                      className={`nav-link ${activeTab === "documents" ? "active" : ""}`}
                      onClick={(e) => handleTabChange("documents", e)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-files me-2"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M15 3v4a1 1 0 0 0 1 1h4" /><path d="M18 17h-7a2 2 0 0 1 -2 -2v-10a2 2 0 0 1 2 -2h4l5 5v7a2 2 0 0 1 -2 2z" /><path d="M16 17v2a2 2 0 0 1 -2 2h-7a2 2 0 0 1 -2 -2v-10a2 2 0 0 1 2 -2h2" /></svg>
                      Files
                    </a>
                  </li>
                </ul>
              </div>

              <div className="card-body">{renderTabContent()}</div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteTarget(null);
        }}
        onConfirm={confirmDelete}
        title={`Delete ${capitalize(deletableTab?.label ?? "record")}`}
        message={`Are you sure you want to delete this ${deletableTab?.label ?? "record"}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        size="md"
      />

      {showPdfModal && (
        <div
          className="modal modal-blur fade show"
          style={{ display: "block" }}
          tabIndex="-1"
          role="dialog"
        >
          <div
            className="modal-dialog modal-fullscreen-sm-down modal-xl modal-dialog-centered"
            role="document"
          >
            <div className="modal-content" style={{ height: "90vh", maxHeight: "90vh" }}>
              <div className="modal-header" style={{ flexShrink: 0 }}>
                <h5 className="modal-title">{pdfTitle}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handlePdfModalClose}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body p-0" style={{ flex: 1, overflow: "hidden" }}>
                {pdfUrl && (
                  <iframe
                    src={pdfUrl}
                    width="100%"
                    height="100%"
                    style={{
                      border: "none",
                      minHeight: "calc(90vh - 120px)",
                      maxHeight: "calc(90vh - 120px)",
                    }}
                    title="PDF Viewer"
                  />
                )}
              </div>
              <div className="modal-footer" style={{ flexShrink: 0 }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handlePdfModalClose}
                >
                  Close
                </button>
                {pdfUrl && (
                  <a
                    href={pdfUrl}
                    download={`${pdfTitle}.pdf`}
                    className="btn btn-primary"
                  >
                    Download PDF
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {showPdfModal &&
        ReactDOM.createPortal(
          <div className="modal-backdrop fade show"></div>,
          document.body,
        )}
    </div>
  );
};

export default Clients;
